import { ChangeSet, Text } from '@codemirror/state';
import { CodeHistory, CodeChangeEvent, CodeChange } from '@yandex-int/k-common/typings';

export interface CodeMirrorExportedState {
    readonly doc: string;
    readonly history: CodeMirrorHistory;
    readonly selection: CodeMirrorHistorySelection;
}

/**
 * Undocumented edit history exported from CodeMirror v6.
 */
interface CodeMirrorHistory {
    /**
     * Undo operations stack (last executed or redone operation is at the end).
     */
    readonly done: readonly CodeMirrorHistoryEvent[];
    /**
     * Redo operations stack (last undone operation is at the end).
     */
    readonly undone: readonly CodeMirrorHistoryEvent[];
}

/**
 * Operation from edit history in CodeMirror v6.
 *
 * `changes` list is present for operations which change the editor text,
 * `ranges` + `prinmIndex` is present for selection changes which are done/undone
 * at the same time as editor text changes.
 */
interface CodeMirrorHistoryEvent {
    /**
     * Editor text change sequence in the same order as when the change was performed
     * (i.e. removing "123" -> "12" -> "1" -> "" will have the changes in the same order).
     */
    readonly changes?: readonly (number | CodeMirrorHistoryChange)[];
    readonly selectionsAfter: CodeMirrorHistorySelection[];
    readonly startSelection?: CodeMirrorHistorySelection;
}

/**
 * Single text change from edit history in CodeMirror v6.
 *
 * The change is defined as: replace text between `from` and `to` positions
 * with the lines from `text`.
 */
type CodeMirrorHistoryChange = [
    /**
     * Number of characters to replace.
     */
    replaceCount: number,
    /**
     * Lines of text to replace the change range with.
     *
     * This lines may contain multiple values which should be joined with a newline.
     * Each line may contain newline characters by itself as well.
     */
    ...text: readonly string[],
];

interface CodeMirrorHistorySelection {
    /* Main selection range index in the `ranges` */
    readonly main: number;
    readonly ranges: readonly CodeMirrorHistorySelectionRange[];
}

interface CodeMirrorHistorySelectionRange {
    readonly anchor: number;
    readonly head: number;
}

/**
 * Exports a text change history from a CodeMirror v6 exported state
 * (from `EditorState.toJSON()`) into a stable documented format.
 *
 * @returns exported edit history which includes recorded undo/redo operations
 * and current (last) text value
 */
export function historyFromRaw(state: CodeMirrorExportedState): CodeHistory {
    const { done: rawDone, undone: rawUndone } = state.history;

    const doc = Text.of([state.doc]);
    const toRedo = reapplyEventsToExport(doc, rawUndone);
    const toUndo = reapplyEventsToExport(doc, rawDone);

    return {
        lastText: state.doc,
        toUndo: toUndo,
        toRedo: toRedo,
    };
}

/**
 * Converts a text change history into a stable format, easily convertible
 * to/from CodeMirror v6 serialized history state.
 *
 * @param codeHistory text change history to convert
 * @returns change history in a stable format and other necessary state
 * to restore CodeMirror v6 state
 */
export function historyToRaw(codeHistory: CodeHistory): CodeMirrorExportedState {
    const doc = Text.of([codeHistory.lastText]);
    const rawUndone = reapplyEventsToImport(doc, codeHistory.toRedo);
    const rawDone = reapplyEventsToImport(doc, codeHistory.toUndo);

    const rawHistory: CodeMirrorHistory = {
        done: rawDone,
        undone: rawUndone,
    };

    return {
        doc: codeHistory.lastText,
        history: rawHistory,
        selection: {
            main: 0,
            ranges: [
                {
                    anchor: codeHistory.lastText.length,
                    head: codeHistory.lastText.length,
                },
            ],
        },
    };
}

/**
 * Checks is code history is self-consistent and non-ambiguous, i.e. produces the same
 * text as the initial one when each event is replayed back and forth.
 */
export function checkHistoryConsistency(codeHistory: CodeHistory): HistoryConsistency {
    try {
        const initial = rewindHistoryTo(codeHistory, 0);
        const last = rewindHistoryTo(initial, initial.toRedo.length);
        if (last.lastText === codeHistory.lastText) {
            return { type: 'consistent' };
        }
        return { type: 'inconsistent', replayed: last };
    } catch (err) {
        return { type: 'error', error: err };
    }
}

export type HistoryConsistency =
    | { type: 'consistent' }
    | { type: 'inconsistent'; replayed: CodeHistory }
    | { type: 'error'; error: unknown };

/**
 * Returns how many operation is recorded as done in the code history
 * (which does not include operations which can be redone).
 */
export function currentHistoryOffset(codeHistory: CodeHistory): number {
    return codeHistory.toUndo.length;
}

/**
 * Returns total operations recorded in the code history, including
 * operations which are already done and ones that can be redone.
 */
export function totalHistoryLength(codeHistory: CodeHistory): number {
    return codeHistory.toUndo.length + codeHistory.toRedo.length;
}

/**
 * Rewinds the history to the given offset in the recorded history of operations.
 *
 * Depending on the speicifed `offset` the history is replayed backward (some operations
 * undone) or forward (some operations are redone).
 *
 * @param codeHistory code history to rewind
 * @param offset offset in the total recorded operations in the history
 * @returns the rewound code history which may have a different text value and
 * some events inverted and moved between done/undone operation stacks
 * @throws {Error} If the offset is out of bounds.
 */
export function rewindHistoryTo(codeHistory: CodeHistory, offset: number): CodeHistory {
    const totalEvents = totalHistoryLength(codeHistory);
    if (offset < 0 || offset > totalEvents) {
        throw new Error(`Expected change index in [0..${totalEvents}]`);
    }

    const currentOffset = currentHistoryOffset(codeHistory);
    if (offset < currentOffset) {
        const doc = Text.of([codeHistory.lastText]);
        const toUndo = codeHistory.toUndo.slice(offset);

        const [changedDoc, invertedEvents] = reapplyEventsToInvert(doc, toUndo);

        // Export and push "redo" stack on top of exisiting "redo" stack
        const addedEvents = attachEventMetadata(invertedEvents, toUndo);
        return {
            ...codeHistory,
            lastText: changedDoc.toString(),
            toUndo: codeHistory.toUndo.slice(0, offset),
            toRedo: [...codeHistory.toRedo, ...addedEvents],
        };
    } else if (offset > currentOffset) {
        const redoCount = offset - currentOffset;
        const redoOffset = codeHistory.toRedo.length - redoCount;
        const doc = Text.of([codeHistory.lastText]);
        const toRedo = codeHistory.toRedo.slice(redoOffset);

        const [changedDoc, invertedEvents] = reapplyEventsToInvert(doc, toRedo);

        // Export and push "undo" stack on top of exisiting "undo" stack
        const addedEvents = attachEventMetadata(invertedEvents, toRedo);
        return {
            ...codeHistory,
            lastText: changedDoc.toString(),
            toUndo: [...codeHistory.toUndo, ...addedEvents],
            toRedo: codeHistory.toRedo.slice(0, redoOffset),
        };
    }
    return codeHistory;
}

/**
 * Applies a stack of raw CodeMirror-specific operations (events) to the text document
 * and exports each one into a stable exported format.
 *
 * @returns a stack of exported events in the same order as the input stack
 */
function reapplyEventsToExport(initialDoc: Text, eventStack: readonly CodeMirrorHistoryEvent[]): CodeChangeEvent[] {
    let doc = initialDoc;
    const exportedEvents: CodeChangeEvent[] = [];
    for (let i = eventStack.length - 1; i >= 0; i -= 1) {
        const raw = eventStack[i];
        const [changedDoc, event] = applyEventToExport(doc, raw);
        if (event) {
            doc = changedDoc;
            exportedEvents.push(event);
        }
    }
    exportedEvents.reverse();
    return exportedEvents;
}

/**
 * Tries to apply a single CodeMirror-specific operation (event) to the text document
 * and export it into a stable format.
 *
 * @returns exported event or `undefined` if the raw event does not contain any text changes
 */
function applyEventToExport(
    initialDoc: Text,
    raw: Pick<CodeMirrorHistoryEvent, 'changes'>
): [Text, CodeChangeEvent | undefined] {
    if (!raw.changes) {
        return [initialDoc, undefined];
    }
    const exportedChanges: CodeChange[] = [];
    let doc = initialDoc;
    let endIndex = doc.length;
    for (let i = raw.changes.length - 1; i >= 0; i -= 1) {
        const rawChange = raw.changes[i];
        if (typeof rawChange === 'number') {
            endIndex -= rawChange;
        } else {
            const [replaceCount, ...lines] = rawChange;
            doc = doc.replace(endIndex - replaceCount, endIndex, lines.length === 0 ? Text.empty : Text.of(lines));
            const change = changeFromRaw(rawChange, endIndex);
            exportedChanges.push(change);
            endIndex = change[0];
        }
    }
    exportedChanges.reverse();
    return [doc, { changes: exportedChanges }];
}

/**
 * Applies a stack of exported operations (events) to the text document
 * and imports each one into a raw CodeMirror-specific format.
 *
 * @returns a stack of exported events in the same order as the input stack
 */
function reapplyEventsToImport(intialDoc: Text, eventStack: readonly CodeChangeEvent[]): CodeMirrorHistoryEvent[] {
    let doc = intialDoc;
    const importedEvents: CodeMirrorHistoryEvent[] = [];
    for (let i = eventStack.length - 1; i >= 0; i -= 1) {
        const event = eventStack[i];
        const [changedDoc, rawEvent] = applyEventToImport(doc, event);
        doc = changedDoc;
        importedEvents.push(rawEvent);
    }
    importedEvents.reverse();
    return importedEvents;
}

/**
 * Applies a single exported operation (event) to the text document
 * and imports it into a CodeMirror-specific format.
 *
 * @returns imported CodeMirror-specific event
 */
function applyEventToImport(doc: Text, event: CodeChangeEvent): [Text, CodeMirrorHistoryEvent] {
    const changeSet = changeSetFromEvent(event, doc.length);
    const changedDoc = changeSet.apply(doc);

    const anchor = lastChangeAnchor(event);
    const selectionsAfter: CodeMirrorHistorySelection[] = [];
    if (anchor !== undefined) {
        selectionsAfter.push({
            main: 0,
            ranges: [{ anchor, head: anchor }],
        });
    }

    return [
        changedDoc,
        {
            changes: changeSet.toJSON(),
            selectionsAfter,
        },
    ];
}

/**
 * Applies a stack of exported operations (events) to the text document
 * and inverts each one.
 *
 * @returns a stack of inverted events in reverse order compared to
 * the input stack with a document text in after applying all events
 */
function reapplyEventsToInvert(
    intialDoc: Text,
    eventStack: readonly CodeChangeEvent[]
): readonly [Text, CodeChangeEvent[]] {
    let doc = intialDoc;
    const invertedEvents: CodeChangeEvent[] = [];
    for (let i = eventStack.length - 1; i >= 0; i -= 1) {
        const event = eventStack[i];
        const changeSet = changeSetFromEvent(event, doc.length);
        const invertedSet = changeSet.invert(doc);
        doc = changeSet.apply(doc);
        const [_, invertedEvent] = applyEventToExport(doc, { changes: invertedSet.toJSON() });
        if (invertedEvent) {
            invertedEvents.push(invertedEvent);
        }
    }
    return [doc, invertedEvents];
}

function changeFromRaw(change: CodeMirrorHistoryChange, endIndex: number): CodeChange {
    const [replaceCount, ...replaceWith] = change;
    // Subtract 1 because the text is indexed from 1 in CodeMirror v6
    const anchorIndex = endIndex - replaceCount;
    return replaceWith.length === 0 ? [anchorIndex, replaceCount] : [anchorIndex, replaceCount, replaceWith];
}

function changeSetFromEvent(event: CodeChangeEvent, documentLength: number): ChangeSet {
    let changeSet = ChangeSet.empty(documentLength);
    for (let i = event.changes.length - 1; i >= 0; i -= 1) {
        const [startIndex, replaceLength, replaceText] = event.changes[i];
        changeSet = changeSet.compose(
            ChangeSet.of(
                {
                    from: startIndex,
                    to: startIndex + replaceLength,
                    insert: replaceText ? Text.of(replaceText) : undefined,
                },
                changeSet.newLength
            )
        );
    }
    return changeSet;
}

/**
 * Returns a last position in text where a change occured by the specified operation.
 *
 * If the operation does not have any text changes, `undefined` is returned.
 */
function lastChangeAnchor(event: CodeChangeEvent): number | undefined {
    if (!event.changes) {
        return undefined;
    }
    for (let i = event.changes.length - 1; i >= 0; i -= 1) {
        const change = event.changes[i];
        return change[0];
    }
    return undefined;
}

function attachEventMetadata(
    changes: readonly Pick<CodeChangeEvent, 'changes'>[],
    metadataStack: readonly CodeChangeEvent[]
): CodeChangeEvent[] {
    if (changes.length !== metadataStack.length) {
        throw new Error('Invalid code history event count to attach metadata to');
    }
    return changes.map((event, i) => {
        const metadata = metadataStack[metadataStack.length - i - 1];
        return { ...metadata, changes: event.changes };
    });
}
